import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, TextField, Typography } from "@material-ui/core";
import { EaterGroupSearch } from "../../components/EaterGroupSearch";
import { Alert } from "@material-ui/lab";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { GROUPS, groupLabel } from "../../accounts";
import { useSysadminData } from "../../SysadminData";
import { Prefill } from "./UserAccount";
import { runSteps, SaveStep } from "./saveAccount";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVITE_GROUPS = [GROUPS.eater, GROUPS.kitchenManager];

/** A long random password nobody knows. The user chooses their own through the reset link. */
function randomPassword(): string {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}


interface Props {
    onClose: () => void;
    prefill?: Prefill;
    /** Called with the email after the user is created. */
    onInvited: (email: string) => void;
    /** Opens an existing user instead, when the email already has an account. */
    onOpenExisting: (email: string) => void;
}

/**
 * Creates a login account (and placement) for a new user and emails them a link to choose their password.
 * The account gets a random password, so it cannot be used before the user has chosen their own.
 */
export const InviteUserDialog: React.FC<Props> = ({ onClose, prefill, onInvited, onOpenExisting }) => {
    const data = useSysadminData();
    const prefillOffice = prefill?.officeId ?? "";
    const [email, setEmail] = useState("");
    const [officeId, setOfficeId] = useState(prefillOffice);
    const [eaterGroupId, setEaterGroupId] = useState(prefill?.eaterGroupId ?? "");
    const [groups, setGroups] = useState<string[]>([GROUPS.eater]);
    const [sendEmail, setSendEmail] = useState(true);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();

    const normalizedEmail = email.trim().toLowerCase();
    const validEmail = EMAIL_PATTERN.test(normalizedEmail);
    const existing = data.serviceUsers.find(s => s.content.id.toLowerCase() === normalizedEmail)?.content.id
        ?? data.users.find(u => (u.content.applicationUserId ?? "").toLowerCase() === normalizedEmail)?.content.applicationUserId;

    const missing: string[] = [];
    if (!validEmail) missing.push("a valid email");
    if (groups.length === 0) missing.push("at least one user group");
    const canInvite = missing.length === 0 && !existing && !saving;

    async function invite() {
        setSaving(true);
        setError(undefined);
        const steps: SaveStep[] = [
            {
                label: "create the login account",
                run: async () => {
                    await planEatApi.postServiceUser({
                        id: normalizedEmail,
                        name: "",
                        description: "",
                        data_raw: { options: {}, authorization: { default: [] }, groups, tags: null, hash: randomPassword(), email: normalizedEmail },
                    });
                },
            },
        ];
        if (eaterGroupId) {
            steps.push({
                label: "place the user",
                run: async () => {
                    await planEatApi.postUser({ id: "", name: "", description: "", applicationUserId: normalizedEmail, officeId, eaterGroupId });
                },
            });
        }
        if (sendEmail) {
            steps.push({ label: "send the invitation email", run: async () => { await planEatApi.postForgotPassword(normalizedEmail); } });
        }

        const result = await runSteps(steps);
        await data.reload();
        setSaving(false);
        if (result.failed) {
            if (result.done.length === 0) {
                setError(`Could not ${result.failed.label}: ${result.failed.message}`);
                return;
            }
            // The account exists, so the rest is done from the user itself
            toast.error(`The user was created, but could not ${result.failed.label}: ${result.failed.message}`, { duration: 8000 });
        } else {
            toast.success(sendEmail ? `Invitation sent to ${normalizedEmail}` : `${normalizedEmail} was created`);
        }
        onInvited(normalizedEmail);
    }

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Invite user</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Creates the user and emails them a link to choose a password. The email is the regular password reset email.
                </Typography>
                {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
                <TextField
                    fullWidth
                    autoFocus
                    variant="outlined"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={() => setTouched(true)}
                    error={(touched && !!email && !validEmail) || !!existing}
                    helperText={existing
                        ? <>This email already has an account. <Link component="button" variant="caption" onClick={() => onOpenExisting(existing)}>Open the user</Link></>
                        : touched && email && !validEmail ? "Not a valid email address" : undefined}
                />

                <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 8 }}>Placement (optional)</Typography>
                <EaterGroupSearch
                    clearable
                    size="small"
                    value={eaterGroupId}
                    onChange={placement => { setOfficeId(placement.officeId); setEaterGroupId(placement.eaterGroupId); }}
                    helperText={eaterGroupId ? undefined : "Leave empty to place the user later"}
                />

                <Typography variant="subtitle2" style={{ marginTop: 24 }}>User groups</Typography>
                {INVITE_GROUPS.map(group => (
                    <FormControlLabel
                        key={group}
                        control={<Checkbox color="primary" checked={groups.includes(group)}
                            onChange={e => setGroups(e.target.checked ? [...groups, group] : groups.filter(g => g !== group))} />}
                        label={groupLabel(group)}
                    />
                ))}
                <Typography variant="caption" color="textSecondary" component="div">
                    Admin rights for companies, offices and eater groups can be given on the user afterwards.
                </Typography>

                <FormControlLabel
                    style={{ marginTop: 16 }}
                    control={<Checkbox color="primary" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />}
                    label="Send the invitation email now"
                />
            </DialogContent>
            <DialogActions>
                {missing.length > 0 && touched && (
                    <Typography variant="body2" color="error" style={{ marginRight: "auto", paddingLeft: 16 }}>Missing: {missing.join(", ")}</Typography>
                )}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!canInvite}
                    onClick={invite}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {sendEmail ? "Invite" : "Create user"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
